// The CRM pane's nine fixed slots (docs/coaching/COLD-OUTBOUND-BASELINE.md section 4b; docs/design/CALL-POP-KRISP-BAR.md
// section 5, lane 5) and its activity log, as a pure function of the call's CRM card. Every slot is present in every
// state with the same reserved number of rows; an empty slot is an empty list, which the pane draws as a quiet dash (or
// a placeholder bar while the card is awaited). The card is another system's text: everything here is cleaned to plain
// text and links are https only, through the same helpers the dock's card uses. crmPaneModel (callWindowModel.ts) still
// owns the title, provenance, dispositions and the primary link; this file adds the slots beside it.
import { DEFAULT_CONTEXT_CARD_DISPOSITIONS, type CallContextCard } from '@bcio-voice/contracts';
import { cleanText, safeLink, type SafeLink } from '../phone/contextCard.ts';
import type { CrmPaneModel } from './callWindowModel.ts';

/** 4b's order. */
export const CRM_SLOT_KEYS = ['who', 'where_we_stand', 'last_time', 'open_loop', 'why_them', 'competitor', 'do_not_say', 'deal', 'links'] as const;
export type CrmSlotKey = typeof CRM_SLOT_KEYS[number];

/** 4b's labels. */
export const CRM_SLOT_LABELS: Readonly<Record<CrmSlotKey, string>> = Object.freeze({
  who: 'Who',
  where_we_stand: 'Where we stand',
  last_time: 'Last time',
  open_loop: 'Open loop',
  why_them: 'Why them',
  competitor: 'Competitor',
  do_not_say: 'Do not say',
  deal: 'Deal',
  links: 'Links',
});

/** Rows each slot reserves, whatever it holds. Who keeps the pane's three rows; Do not say has three, one landmine per row
 * (review round 1: joined, the third was cut); the rest one or two. CrmPane.tsx reserves 13 rows for slots 2 to 8. */
export const CRM_SLOT_ROWS: Readonly<Record<CrmSlotKey, 1 | 2 | 3>> = Object.freeze({
  who: 3, where_we_stand: 2, last_time: 2, open_loop: 2, why_them: 2, competitor: 1, do_not_say: 3, deal: 1, links: 1,
});

/**
 * How a slot fills its rows: 'rows' puts each entry on its own row (cut with an ellipsis), 'clamp' joins the entries
 * into one text clamped to the reserved rows, and 'flow' starts each entry on a new line and wraps it, clamped to the
 * reserved rows: short entries get a row each, and a long first entry takes the rows it needs before the next one.
 * Last time flows (review round 3): it shows the previous call's wrap-up when the CRM sends one, with the date ("Oct 22:
 * They redid the site last year and new patient calls are flat."), and the latest email otherwise. bcio-net sends the
 * wrap-up for Brandcore again behind its own switch, on by default (Allen, October 31: any rep picks up where the last
 * one left off). A wrap-up line runs about 80 characters where one row of the pane holds about 43; as its own nowrap row
 * the point ("new patient calls are flat") was cut.
 */
export type CrmSlotMode = 'rows' | 'clamp' | 'flow';
export const CRM_SLOT_MODE: Readonly<Record<CrmSlotKey, CrmSlotMode>> = Object.freeze({
  who: 'rows', where_we_stand: 'clamp', last_time: 'flow', open_loop: 'clamp', why_them: 'clamp', competitor: 'clamp', do_not_say: 'rows', deal: 'clamp', links: 'rows',
});

export interface CrmLink extends SafeLink { label: string }

export interface CrmSlot {
  key: CrmSlotKey;
  label: string;
  rows: 1 | 2 | 3;
  mode: CrmSlotMode;
  /** What the slot says: one entry per row ('rows'), or the entries to join ('clamp'). Empty is a dash. Who keeps its
   * three positions, null where a row has nothing. */
  lines: (string | null)[];
  /** The deal's deck (Deal) and the card's links (Links); empty elsewhere. */
  links: CrmLink[];
}

export interface CrmActivityRow { key: string; type: string; summary: string; when: string; at: string | null }

export interface CrmSlotsModel {
  /** Exactly CRM_SLOT_KEYS, in order. */
  slots: CrmSlot[];
  /** The card's history, newest first: the pane's activity log (a fixed-height box that scrolls inside itself). */
  activity: CrmActivityRow[];
  /** The card carried the CRM's own slots (a CRM release that predates them sends none; the pane then shows what the
   * older card has: who, the next step as the open loop, the stage and owner as the deal, the history). */
  fromSlots: boolean;
}

/** The text a clamped slot shows: its entries joined with semicolons (never a middle dot, which reads as a glyph). */
export function slotText(slot: Pick<CrmSlot, 'lines'>): string {
  return slot.lines.filter((l): l is string => !!l).join('; ');
}

const ACTIVITY_TYPES: Readonly<Record<string, string>> = Object.freeze({
  call: 'Call', 'call.ended': 'Call', 'call.disposition': 'Disposition', note: 'Note', email: 'Email', sms: 'Text',
  text: 'Text', meeting: 'Meeting', form: 'Form', proposal: 'Proposal', ticket: 'Ticket', stage_change: 'Stage',
  triage: 'Stage', voicemail: 'Voicemail', created: 'Created', system: 'Update', event: 'Event',
});

function activityType(raw: unknown): string {
  const t = cleanText(raw).toLowerCase();
  if (!t) return 'Event';
  const known = ACTIVITY_TYPES[t];
  if (known) return known;
  const words = t.replace(/[._-]+/g, ' ').trim();
  return (words.charAt(0).toUpperCase() + words.slice(1)).slice(0, 14);
}

const DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const DAY_YEAR = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** "Oct 22", or "Oct 22, 2025" outside the current year. Text that is not a date is shown cleaned and short. */
export function activityWhen(at: string | null, now: number = Date.now()): string {
  if (!at) return '';
  const t = Date.parse(at);
  if (!Number.isFinite(t)) return cleanText(at).slice(0, 12);
  const d = new Date(t);
  return d.getFullYear() === new Date(now).getFullYear() ? DAY.format(d) : DAY_YEAR.format(d);
}

const nonEmpty = (v: unknown): string | null => { const s = cleanText(v); return s ? s : null; };
const list = (v: unknown, max: number): string[] => {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const item of v) {
    const s = cleanText(item);
    if (s && !out.includes(s)) out.push(s);
    if (out.length >= max) break;
  }
  return out;
};

const NUMBER_KIND: Readonly<Record<string, string>> = Object.freeze({ mobile: 'mobile', front_desk: 'front desk' });

/** The card's links for the Links slot: https only, the first (the CRM's own page) labelled CRM. */
function cardLinks(card: CallContextCard | null): CrmLink[] {
  const out: CrmLink[] = [];
  for (const l of Array.isArray(card?.links) ? card!.links : []) {
    const safe = safeLink(l?.url);
    if (!safe || out.some(o => o.href === safe.href)) continue;
    const label = cleanText(l?.label);
    const short = out.length === 0 ? 'CRM'
      : /maps?\b/i.test(label) || /(^|\.)maps\./i.test(`${safe.host}.`) || /google\.[a-z.]+\/maps/i.test(safe.href) ? 'Maps'
        : /web ?site|^site$/i.test(label) ? 'Website'
          : (label || safe.host).slice(0, 24);
    out.push({ ...safe, label: short });
    if (out.length >= 4) break;
  }
  return out;
}

// A contact card from the CRM carries its recent activity as "Recent" fields: "Oct 20, 2026", a middle dot, "Incoming email: Thanks...".
const RECENT_FIELD = /^(.+?)\s+[\u00b7\u2022,]\s+([^:]+?)(?::\s+(.*))?$/;

/** The card's history; a card without one (a CRM contact card) offers its "Recent" fields instead. */
function rawActivity(card: CallContextCard | null): { type: unknown; summary: unknown; at: unknown }[] {
  const history = Array.isArray(card?.history) ? card!.history : [];
  if (history.length) return history;
  const out: { type: unknown; summary: unknown; at: unknown }[] = [];
  for (const f of Array.isArray(card?.fields) ? card!.fields : []) {
    if (cleanText(f?.label).toLowerCase() !== 'recent' || typeof f?.value !== 'string') continue;
    const m = RECENT_FIELD.exec(f.value.trim());
    if (!m) continue;
    const what = m[2]!.replace(/\(.*?\)/g, '').trim();
    out.push({ type: what.split(/\s+/).pop(), summary: m[3] || m[2], at: m[1] });
  }
  return out;
}

// bcio-net writes a disposition's history line as "Call disposition: callback_scheduled" (prospecting/sidetone-lookup.ts).
const DISPOSITION_SUMMARY = /^(?:call\s+)?disposition\s*:\s*/i;
const DISPOSITION_ID = /^[a-z0-9_:-]{1,60}$/;

/**
 * A disposition row's summary is the disposition's label ("Callback scheduled"): never the CRM's raw id, and never the
 * word the type column already says (review round 2). The label comes from the card's own options, else the contract's
 * defaults (the ones bcio-net logs), else the id in words. Any other row keeps its summary.
 */
export function activityLine(type: string, summary: string, options: readonly { id: string; label: string }[] = []): { type: string; summary: string } {
  const prefixed = DISPOSITION_SUMMARY.test(summary);
  if (type !== 'Disposition' && !prefixed) return { type, summary };
  const rest = summary.replace(DISPOSITION_SUMMARY, '').trim();
  if (!rest) return { type, summary };
  if (!DISPOSITION_ID.test(rest)) return { type: 'Disposition', summary: rest };
  const known = options.find(o => o?.id === rest) ?? DEFAULT_CONTEXT_CARD_DISPOSITIONS.find(o => o.id === rest);
  const label = known ? cleanText(known.label) : '';
  const words = rest.replace(/[_:-]+/g, ' ').trim();
  return { type: 'Disposition', summary: label || (words ? words.charAt(0).toUpperCase() + words.slice(1) : rest) };
}

/** Newest first; entries without a readable date keep the card's order after the dated ones. */
function activityRows(card: CallContextCard | null, now: number): CrmActivityRow[] {
  const options = Array.isArray(card?.dispositions) ? card!.dispositions : [];
  const rows = rawActivity(card)
    .map((h, i) => ({ i, ...activityLine(activityType(h?.type), cleanText(h?.summary), options), at: nonEmpty(h?.at) }))
    .filter(r => r.summary);
  const time = (at: string | null) => { const t = at ? Date.parse(at) : NaN; return Number.isFinite(t) ? t : null; };
  rows.sort((a, b) => {
    const ta = time(a.at), tb = time(b.at);
    if (ta !== null && tb !== null) return tb - ta || a.i - b.i;
    if (ta !== null) return -1;
    if (tb !== null) return 1;
    return a.i - b.i;
  });
  return rows.map(r => ({ key: `${r.i}-${r.type}`, type: r.type, summary: r.summary, when: activityWhen(r.at, now), at: r.at }));
}

function dealLine(stage: string | null, amount: string | null, owner: string | null): string | null {
  const parts = [stage ? `Stage ${stage}` : null, amount, owner ? `owner ${owner}` : null].filter((p): p is string => !!p);
  return parts.length ? parts.join(', ') : null;
}

/**
 * The nine slots and the activity log for the card the pane shows (the call's own, or the last call's with this
 * number). `card` is the raw card the CRM pinned; `crm` is the pane's model for the same card, which already decided
 * what the who rows say and whether the card is still awaited.
 */
export function crmSlotsModel(card: CallContextCard | null | undefined, crm: Pick<CrmPaneModel, 'who' | 'state' | 'nextStep' | 'history' | 'primary' | 'details'>, now: number = Date.now()): CrmSlotsModel {
  const c = card ?? null;
  // `undefined`: the window has not handed the pane the raw card, so the slots come from the pane's model alone.
  const unwired = card === undefined;
  const s = c?.slots && typeof c.slots === 'object' ? c.slots : null;
  const rawWho = c?.who && typeof c.who === 'object' ? c.who : null;
  // Local time is the prospect's clock when the card was built: on a card carried from an earlier call it is stale.
  const local = s && crm.state !== 'previous' ? nonEmpty(s.local_time) : null;
  const kind = s?.number_kind ? NUMBER_KIND[s.number_kind] ?? null : null;
  const where = local && kind ? `${local}, ${kind}` : local ?? (kind ? `${kind.charAt(0).toUpperCase()}${kind.slice(1)} number` : null);
  const deal = s?.deal && typeof s.deal === 'object' ? s.deal : null;
  const deck = deal ? safeLink(deal.deck_url) : null;
  const dealText = s
    ? dealLine(nonEmpty(deal?.stage), nonEmpty(deal?.amount), nonEmpty(deal?.owner))
    : rawWho ? dealLine(nonEmpty(rawWho.stage), null, nonEmpty(rawWho.owner)) : unwired ? crm.who[2] : null;
  // A card without `who` (a CRM contact card, a generic card) names the person and company in its fields.
  const field = (...labels: string[]) => crm.details.find(d => labels.includes(d.label.toLowerCase()) && d.value)?.value ?? null;
  const person = crm.who[0] ?? ([field('name', 'contact'), field('title', 'role')].filter(Boolean).join(', ') || null);
  const lines: Record<CrmSlotKey, (string | null)[]> = {
    who: [person, crm.who[1] ?? field('company'), where],
    where_we_stand: s ? list([s.where_we_stand], 1) : [],
    last_time: s ? list(s.last_time, 2) : [],
    // An older card's next step is its open loop.
    open_loop: s ? list([s.open_loop], 1) : crm.nextStep ? [crm.nextStep] : [],
    why_them: s ? list(s.why_them, 3) : [],
    competitor: s ? list([s.competitor], 1) : [],
    do_not_say: s ? list(s.do_not_say, 3) : [],
    deal: dealText ? [dealText] : [],
    links: [],
  };
  const slots = CRM_SLOT_KEYS.map((key): CrmSlot => ({
    key,
    label: CRM_SLOT_LABELS[key],
    rows: CRM_SLOT_ROWS[key],
    mode: CRM_SLOT_MODE[key],
    lines: lines[key],
    links: key === 'deal' ? (deck ? [{ ...deck, label: 'Deck' }] : [])
      : key === 'links' ? (unwired ? (crm.primary ? [{ href: crm.primary.href, host: crm.primary.host, label: 'CRM' }] : []) : cardLinks(c))
        : [],
  }));
  const activity = unwired
    ? crm.history.map((h, i) => ({ key: `${i}`, type: '', summary: h.summary, when: h.when, at: null }))
    : activityRows(c, now);
  return { slots, activity, fromSlots: !!s };
}

/** True when a slot has nothing to show: the pane draws its quiet dash. */
export function slotEmpty(slot: CrmSlot): boolean {
  return !slot.lines.some(l => !!l) && !slot.links.length;
}
